﻿import { Box, Grid2, Tab, Tabs, TextField, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { PercentField } from "@/shared/components/PercentField";
import {
	MILL_1,
	type MillConfig,
	type MillId,
} from "@/setup/mill-config/mill-config";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { round } from "lodash-es";
import { SectionFamilySelector } from "../../../../../shared/selectors";
import { Section, SECTION_INITIAL_VALUES } from "../../../models/Section";
import { upsertSection } from "../../../repositories/SectionRepository";

type Props = {
	section: Section | null;
	modal: "create" | "update" | "delete" | "";
	assetId: number;
	availableMills: MillConfig[];
	showMillTabs: boolean;
	onMillChange: (millId: MillId) => void;
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
	children?: ReactNode;
};

const resolveTargetTPH = ({
	baseTargetTPH,
	targetTPHPercentage,
}: {
	baseTargetTPH?: number | null;
	targetTPHPercentage?: number | null;
}) => {
	if (baseTargetTPH == null) return null;

	const resolvedPercentage = targetTPHPercentage ?? 80;

	return round((baseTargetTPH * resolvedPercentage) / 100, 2);
};

const parseNumberFieldValue = (value: string) => {
	if (value === "") return null;

	const parsedValue = Number(value);

	return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const SectionDetails = ({
	section,
	modal,
	assetId,
	availableMills,
	showMillTabs,
	onMillChange,
	onHide,
	children,
}: Props) => {
	const isM1 = assetId === MILL_1.id;
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const {
		control,
		reset,
		handleSubmit,
		watch,
		formState: { dirtyFields },
	} = useForm<Section>({
		mode: "all",
	});

	const targetTPH = watch("TargetTPH");
	const targetTPHPercentage = watch("TargetTPHPercentage");
	const resolvedTargetTPH = resolveTargetTPH({
		baseTargetTPH: targetTPH,
		targetTPHPercentage,
	});
	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertSection, {
		meta: {
			successMessage: `The section was ${
				modal === "create" ? "created" : "updated"
			} successfully`,
		},
		onSuccess: async (id: number) => {
			await queryClient.invalidateQueries({ queryKey: ["sections"] });
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Section> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			upsertMutation.mutate({ section: data, defaultAreaId: assetId });
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(section || SECTION_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, section]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Section - Sections`}
				/>
			)}
			{showMillTabs && (
				<Tabs
					value={assetId}
					onChange={(_, millId: MillId) => {
						if (!isFormDirty) onMillChange(millId);
					}}
					aria-label="Section mill details"
					aria-describedby={
						isFormDirty ? "section-mill-switch-help" : undefined
					}
					variant="fullWidth"
					sx={{ borderBottom: 1, borderColor: "divider" }}
				>
					{availableMills.map((mill) => (
						<Tab
							key={mill.id}
							id={`section-mill-tab-${mill.id}`}
							aria-controls="upsert-section-form"
							value={mill.id}
							label={mill.name}
							disabled={isFormDirty && mill.id !== assetId}
						/>
					))}
				</Tabs>
			)}
			{showMillTabs && isFormDirty && (
				<Typography
					id="section-mill-switch-help"
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", px: 2, py: 0.75 }}
				>
					Save or close the section changes before switching mills.
				</Typography>
			)}
			{section === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a section or create a new one</Typography>
				</div>
			) : (
				<Box
					component="form"
					id="upsert-section-form"
					role={showMillTabs ? "tabpanel" : undefined}
					aria-labelledby={
						showMillTabs ? `section-mill-tab-${assetId}` : undefined
					}
					onSubmit={handleSubmit(onSubmit)}
					sx={{ p: 1 }}
				>
					<Grid2 container spacing={1} p={1}>
						<Grid2 size={{ xs: 12, md: isM1 ? 3.2 : 1.8 }}>
							<Controller
								name={"SectionName"}
								control={control}
								rules={{ required: "Name is required" }}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										margin="none"
										fullWidth
										size="small"
										value={value}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{!isM1 ? (
							<Grid2 size={{ xs: 12, md: 3 }}>
								<Controller
									name="SectionFamilyId"
									rules={{ required: "Family is required" }}
									control={control}
									render={({ field, fieldState: { error } }) => (
										<SectionFamilySelector
											assetId={assetId}
											label="Section Family"
											value={field.value}
											onChange={field.onChange}
											disabled={modal === "delete"}
											fieldError={error}
											required
										/>
									)}
								/>
							</Grid2>
						) : null}
						<Grid2 size={{ xs: 12, md: isM1 ? 2.2 : 1.8 }}>
							<Controller
								name="TonsPerHour"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Budget TPH"
										type="number"
										variant="outlined"
										margin="none"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => {
											onChange(parseNumberFieldValue(e.target.value));
										}}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: isM1 ? 6.6 : 5.4 }}>
							<Grid2 container spacing={1}>
								<Grid2 size={4}>
									<Controller
										name="TargetTPH"
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<TextField
												label="Target TPH"
												type="number"
												variant="outlined"
												margin="none"
												fullWidth
												size="small"
												autoComplete="off"
												value={value ?? ""}
												onChange={(e) => {
													onChange(parseNumberFieldValue(e.target.value));
												}}
												error={!!error}
												helperText={error?.message}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={4}>
									<Controller
										name="TargetTPHPercentage"
										control={control}
										rules={{
											validate: (value) => {
												if (value == null) return true;

												if (value <= 0) {
													return "Must be greater than 0%";
												}
												return true;
											},
										}}
										render={({ field, fieldState: { error } }) => (
											<PercentField
												label="TPH Pct."
												value={field.value}
												onChange={(value) => {
													field.onChange(value);
												}}
												error={!!error}
												helperText={error?.message}
												maxValue={null}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={4}>
									<TextField
										label="Effective TPH"
										type="number"
										variant="outlined"
										margin="none"
										fullWidth
										size="small"
										autoComplete="off"
										value={resolvedTargetTPH ?? ""}
										disabled
									/>
								</Grid2>
							</Grid2>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4.8 }}>
							<Controller
								name="Description"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Description"
										variant="outlined"
										margin="none"
										autoComplete="off"
										fullWidth
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 1.8 }}>
							<Controller
								name="LBsPerFoot"
								control={control}
								rules={{
									min: {
										value: 0,
										message: "Must be at least 0",
									},
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="LBs / Foot"
										type="number"
										variant="outlined"
										margin="none"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) =>
											onChange(
												e.target.value === "" ? null : Number(e.target.value),
											)
										}
										error={!!error}
										helperText={error?.message}
										InputProps={{
											inputProps: {
												step: "any",
												min: 0,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 1.8 }}>
							<Controller
								name="BilletSize"
								control={control}
								rules={{
									min: {
										value: 1,
										message: "Must be at least 1",
									},
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Billet Size"
										type="number"
										margin="none"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
										InputProps={{
											inputProps: {
												step: 1,
												min: 1,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 1.8 }}>
							<Controller
								name="MillYield"
								control={control}
								rules={{
									min: {
										value: 0,
										message: "Must be at least 0%",
									},
									max: {
										value: 100,
										message: "Must be less than or equal to 100%",
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<PercentField
										label="Mill Yield"
										value={field.value ?? 0}
										error={!!error}
										onChange={field.onChange}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 1.8 }}>
							<Controller
								name="FinishedYield"
								control={control}
								rules={{
									min: {
										value: 0,
										message: "Must be at least 0%",
									},
									max: {
										value: 100,
										message: "Must be less than or equal to 100%",
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<PercentField
										label="Finished Yield"
										value={field.value ?? 0}
										error={!!error}
										onChange={field.onChange}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{children ? <Grid2 size={12}>{children}</Grid2> : null}
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</Box>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
